import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import { FiZap, FiEye } from "react-icons/fi";
import "./ProductionLines.scss";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const ProductionLines = () => {
  useTitle("Rovion MES - Üretim Hatları");
  const navigate = useNavigate();
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/production-lines",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );
  const [lines, setLines] = useState([]);
  const [dailyQtyByOutputId, setDailyQtyByOutputId] = useState({});
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const allowedMachineIds = useMemo(() => {
    const list = currentUser?.machineAccess || [];
    if (!Array.isArray(list) || list.length === 0) return null;
    return new Set(list.map((x) => String(x)));
  }, [currentUser]);

  const addToast = useCallback((message, type = "info") => {
    const t = { id: Date.now(), message, type };
    setToasts((p) => [...p, t]);
    setTimeout(() => setToasts((p) => p.filter((x) => x.id !== t.id)), 3000);
  }, []);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const { headers } = authHeaders();
      const linesRes = await axios.get(
        `${API_URL}/api/production-lines/from-parts?finalOnly=true`,
        { headers },
      );
      const start = new Date();
      start.setUTCHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setUTCHours(23, 59, 59, 999);
      const dailyRes = await axios.get(`${API_URL}/api/daily-production`, {
        headers,
        params: { from: start.toISOString(), to: end.toISOString() },
      });
      setLines(linesRes.data?.lines || linesRes.data || []);
      const map = {};
      const items = dailyRes.data?.items || [];
      items.forEach((it) => {
        const oid = it.outputItemId?._id || it.outputItemId;
        if (!oid) return;
        const key = String(oid);
        map[key] = (map[key] || 0) + (Number(it.quantity) || 0);
      });
      setDailyQtyByOutputId(map);
    } catch (err) {
      addToast("Veriler yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, addToast]);

  const handleView = useCallback(
    (line) => navigate(`/production-lines/view/${line._id}`),
    [navigate],
  );

  const getFinalOutputLabel = (line) => {
    const out = line?.outputPartId;
    if (!out) return line?.name || "-";
    const name = out.name || line?.name || "";
    const code = out.code || line?.code || "";
    if (name && code) return `${name} (${code})`;
    return name || code || "-";
  };

  const getTodayProduced = (line) => {
    const outId = line?.outputItemId ? String(line.outputItemId) : null;
    if (!outId) return 0;
    return dailyQtyByOutputId[outId] || 0;
  };

  const getMachinesSummary = (line) => {
    const items = line.machineItems || [];
    if (!items.length) return "-";
    return items
      .map((mi) => mi.machineId?.name || mi.machineId?.code || "?")
      .join(" → ");
  };

  const headers = [
    {
      name: "Hat Adı",
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        String(getFinalOutputLabel(a) || "").localeCompare(
          String(getFinalOutputLabel(b) || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    },
    {
      name: "Kod",
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a.code || "").localeCompare(String(b.code || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        }),
    },
    {
      name: "Tip",
      dataType: "text",
      id: 2,
      customSorter: (a, b) =>
        String(a.productLineType || "").localeCompare(
          String(b.productLineType || ""),
        ),
    },
    {
      name: "Giriş Tipi",
      dataType: "text",
      id: 3,
      customSorter: (a, b) =>
        String(a.dataEntryType || "").localeCompare(
          String(b.dataEntryType || ""),
        ),
    },
    {
      name: "Bugün Üretim",
      dataType: "number",
      id: 4,
      customSorter: (a, b) => getTodayProduced(a) - getTodayProduced(b),
    },
    { name: "İşlemler", id: 5, sortable: false },
  ];

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("production-lines-list", headers);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const visibleLines = useMemo(() => {
    if (!allowedMachineIds) return lines || [];
    return (lines || []).filter((line) =>
      (line.machineItems || []).some((mi) => {
        const mid = mi.machineId?._id || mi.machineId;
        return mid && allowedMachineIds.has(String(mid));
      }),
    );
  }, [lines, allowedMachineIds]);

  return (
    <AppPage
      title="Üretim Hatları"
      subtitle="Üretim hatları Parça–Makine tanımlarından otomatik oluşturulur (sadece final parçalar)"
      icon={<FiZap />}
    >
      <CardContainer>
        {loading ? (
          <div className="loading-container">
            <FiZap className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <>
            <Table
              columns={headers}
              visibleColumns={visibleColumns}
              tableId="production-lines-list"
              checkboxSelection
              loading={loading}
              viewOptions={
                <ListViewOptionsPanel
                  columns={headers}
                  visibleColumns={visibleColumns}
                  onToggleColumn={toggleColumnVisibility}
                  onResetColumns={resetToDefault}
                  showApply={false}
                  showReset={false}
                />
              }
            >
              {visibleLines.map((line) => (
                <Tr
                  key={line._id}
                  id={line._id}
                  data={line}
                  onClick={() => handleView(line)}
                >
                  {visibleColumns.includes(0) && (
                    <Td>
                      <strong>{getFinalOutputLabel(line)}</strong>
                      {line.name && (
                        <div className="text-muted small mt-1">{line.name}</div>
                      )}
                    </Td>
                  )}
                  {visibleColumns.includes(1) && <Td>{line.code}</Td>}
                  {visibleColumns.includes(2) && (
                    <Td>
                      {(line.productLineType || "production") === "selling"
                        ? "Sipariş"
                        : "Üretim"}
                    </Td>
                  )}
                  {visibleColumns.includes(3) && (
                    <Td>
                      {line.dataEntryType === "from_machine"
                        ? "Makineden"
                        : "Manuel"}
                    </Td>
                  )}
                  {visibleColumns.includes(4) && (
                    <Td>{getTodayProduced(line)}</Td>
                  )}
                  {visibleColumns.includes(5) && (
                    <Td onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        icon={<FiEye style={{ fontSize: "18px" }} />}
                        appearance="subtle"
                        shape="circle"
                        onClick={() => handleView(line)}
                        title="Üretim Hattı Akışı"
                      />
                    </Td>
                  )}
                </Tr>
              ))}
            </Table>
            {(!visibleLines || visibleLines.length === 0) && (
              <div className="empty-state">
                <FiZap size={64} />
                <p>Üretim hattı bulunamadı</p>
                <p className="text-muted small mt-1">
                  Üretim hatları, `Parçalar` ve `Parça–Makine` tanımlarından
                  otomatik çıkarılır.
                </p>
              </div>
            )}
          </>
        )}
      </CardContainer>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type}
            onClose={() => setToasts((p) => p.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(ProductionLines, ["admin", "employee", "user"]);
